import { ReactNode, useCallback } from "react";
import { RouterProvider as ReactAriaRouterProvider } from "react-aria-components";
import {
  NavigateFunction,
  NavigateOptions,
  To,
  useHref as useReactRouterHref,
  useNavigate as useReactRouterNavigate,
} from "react-router-dom";

type NcRouterProviderProps = {
  children: ReactNode;
};

const isToAbsolute = (to: To): to is string =>
  typeof to === "string" &&
  (to.startsWith("https://") || to.startsWith("http://") || to.startsWith("mailto:"));

const isToHash = (to: To): to is string => typeof to === "string" && to.startsWith("#");

// Resolve a relative internal path (e.g. '..', '../x', 'sub/page') against current location.
const resolveRelativePath = (to: To) => {
  if (typeof to !== "string") return to;
  if (to.startsWith("/") || to.startsWith("?")) return to;
  const currentPath = window.location.pathname;
  const baseSegments = currentPath.split("/").filter(Boolean);
  const toSegments = to.split("/");
  for (const seg of toSegments) {
    if (!seg || seg === ".") continue;
    if (seg === "..") {
      baseSegments.pop();
    } else {
      baseSegments.push(seg);
    }
  }
  return "/" + baseSegments.join("/");
};

// See https://github.com/adobe/react-spectrum/issues/6397.
const useHref = (to: To): string => {
  const reactRouterHref = useReactRouterHref(to);
  return isToAbsolute(to) ? to : reactRouterHref;
};

const useNavigate = (): NavigateFunction => {
  const reactRouterNavigate = useReactRouterNavigate();
  return useCallback(
    ((to: To, options?: NavigateOptions) => {
      if (isToAbsolute(to) || isToHash(to)) {
        window.location.href = to;
        return;
      }
      return reactRouterNavigate(resolveRelativePath(to), options);
    }) as NavigateFunction,
    [reactRouterNavigate]
  );
};

export const NcRouterProvider = ({ children, ...props }: NcRouterProviderProps) => {
  const navigate = useNavigate();
  return (
    <ReactAriaRouterProvider navigate={navigate} useHref={useHref} {...props}>
      {children}
    </ReactAriaRouterProvider>
  );
};
